import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { DataBagService } from '@/common/data-bag.service';
import { OrganizationService } from '../organization.service';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '@/common/loader/loader.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { OrganizationAddressEditComponent } from './organization-address-edit/organization-address-edit.component';
import { OrganizationAddressAddComponent } from './organization-address-add/organization-address-add.component';
import { OrganizationGusImportService } from '../organization-data-download/organization-gus-import.service';
import { AddressKind } from '@/common/enums/AddressKind';
import {
	ContactDataInterface,
	OrganizationAddress,
	OrganizationContactAddEditDialog,
	OrganizationModel,
} from '@/organization/organization/organization.model';
import { OrganizationContactAddEditDialogComponent } from '@/organization/organization/organization-primary/organization-contact-add-edit-dialog/organization-contact-add-edit-dialog.component';
import { PageFunction } from '@/common/enums/PageFunction';

@Component({
	selector: 'app-organization-primary',
	templateUrl: './organization-primary.component.html',
	styleUrls: ['./organization-primary.component.css'],
})
export class OrganizationPrimaryComponent implements OnInit {
	@Input() pageFunction: PageFunction;
	@ViewChild('dataTableAddress') tableAddress: MatTable<any>;
	@ViewChild('dataTableContacts') tableContacts: MatTable<any>;
	matDataAddress = new MatTableDataSource<any>();
	matDataContacts = new MatTableDataSource<any>();
	editable = false;

	addressColumns = ['address', 'zipCode', 'location', 'kind', 'edit'];

	contactColumns = ['department', 'departmentNumber', 'departmentEmail', 'userResponsibility', 'edit'];

	cooperationSet = [
		{
			key: 'CO',
			value: 'Stała',
		},
		{
			key: 'OC',
			value: 'Okazjonalna',
		},
	];

	addressMapping: Record<AddressKind, string> = {
		[AddressKind.HEADQUATERS]: 'Siedziba',
		[AddressKind.RESIDENCE]: 'RSDN',
		[AddressKind.CHECKINS]: 'CHCK',
		[AddressKind.SERVICE]: 'SRVC',
		[AddressKind.WORKSHOP]: 'WRKSHP',
		[AddressKind.OFFICE]: 'OFFC',
	};

	organizationService = inject(OrganizationService);

	private readonly parentComponent = 'organization';
	private apiUrlAdd = '/organization/';
	private apiUrl = '/organization/';

	private readonly bag = inject(DataBagService);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly loader = inject(LoaderService);
	private readonly infobox = inject(InfoboxService);
	private readonly dialog = inject(MatDialog);
	private readonly gusImport = inject(OrganizationGusImportService);

	ngOnInit(): void {
		this.organizationService.pageTitle = 'Organizacja';
		this.editable = this.pageFunction === PageFunction.ADD;
		this.matDataAddress.data = this.organizationService.organizationData.address;
		if (!this.route.snapshot.params['id']) {
			this.router.navigate([this.parentComponent]);
		}
		if (this.route.snapshot.params['id'] === PageFunction.ADD) {
			this.pageFunction = PageFunction.ADD;
			this.editable = true;
			if (!this.apiUrlAdd) {
				this.apiUrlAdd = `${this.parentComponent}/`;
			}
			this.loader.hide();
		} else {
			this.pageFunction = PageFunction.EDIT;
			this.organizationService.organizationData.id = this.route.snapshot.params['id'];
			this.apiUrl = `/organization/${this.organizationService.organizationData.id}/`;
		}

		if (this.pageFunction === PageFunction.EDIT) {
			this.bag.get(this.apiUrl, (result: OrganizationModel, error: Error) => {
				if (error) {
					return;
				}
				this.organizationService.organizationData = result;
				this.organizationService.travelOrganizationGovernmentApproval.set(
					result.travelOrganizationGovernmentApproval,
				);
				this.matDataAddress.data = this.organizationService.organizationData.address;
				this.matDataContacts.data = this.organizationService.organizationData.departments;
				this.organizationService.pageTitle = this.organizationService.pageTitleGenerate();
				this.loader.hide();
			});
		}

		this.bag.get('/sell_category/?limit=999', (result) => {
			this.organizationService.enterpriseData.sellCategory = result.results;
		});
	}

	submitOrganization(): void {
		this.loader.show();
		switch (this.pageFunction) {
			case PageFunction.ADD:
				this.doAdd(this.dataSendGenerate());
				break;
			case PageFunction.EDIT:
				this.doEdit(this.dataSendGenerate());
				break;
		}
	}

	openAddressAdd(): void {
		this.dialog
			.open(OrganizationAddressAddComponent)
			.afterClosed()
			.subscribe(() => {
				this.matDataAddress.data = this.organizationService.organizationData.address;
				this.tableAddress.renderRows();
			});
	}

	openAddressEdit(contact: OrganizationAddress): void {
		this.dialog
			.open(OrganizationAddressEditComponent, {
				data: contact,
			})
			.afterClosed()
			.subscribe(() => {
				this.matDataAddress.data = this.organizationService.organizationData.address;
				this.tableAddress.renderRows();
			});
	}

	openContactAdd(): void {
		const dialogData: OrganizationContactAddEditDialog = {
			mode: PageFunction.ADD,
		};
		this.dialog
			.open(OrganizationContactAddEditDialogComponent, {
				data: dialogData,
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					this.organizationService.organizationData.departments.push(result);
					this.matDataContacts.data = this.organizationService.organizationData.departments;
					this.tableContacts.renderRows();
				}
			});
	}

	openContactEdit(contact: ContactDataInterface): void {
		const dialogData: OrganizationContactAddEditDialog = {
			mode: PageFunction.EDIT,
			organizationData: contact,
			id: contact.id,
		};

		this.dialog
			.open(OrganizationContactAddEditDialogComponent, {
				data: dialogData,
			})
			.afterClosed()
			.subscribe((result) => {
				if (!result) {
					return;
				}
				this.organizationService.organizationData.departments =
					this.organizationService.organizationData.departments.map((department) => {
						return {
							...department,
							departmentContacts:
								department.id === result.id ? result.departmentContacts : department.departmentContacts,
							kind: department.id === result.id ? result.kind : department.kind,
							userResponsibility:
								department.id === result.id ? result.userResponsibility : department.userResponsibility,
						};
					});

				this.matDataContacts.data = this.organizationService.organizationData.departments;
				this.tableContacts.renderRows();
			});
	}

	openGusDialog(): void {
		this.gusImport.importCompany().subscribe(() => {
			this.matDataAddress.data = this.organizationService.organizationData.address;
			this.tableAddress.renderRows();
		});
	}

	editUnlock(): void {
		this.editable = true;
	}

	private dataSendGenerate() {
		return {
			...this.organizationService.organizationData,
			id: null,
			sellCategory: this.organizationService.organizationData.isOskar
				? null
				: this.organizationService.organizationData.sellCategory,
			address: this.organizationService.organizationData.address.map((singleAddress) => {
				return {
					location: singleAddress.location?.id,
					address: singleAddress.address,
					zip_code: singleAddress.zipCode,
					kind: singleAddress.kind,
				};
			}),
			departments: this.organizationService.organizationData.departments.map((singleDepartment) => {
				return {
					id: singleDepartment.id ? singleDepartment.id : null,
					kind: singleDepartment.kind.id ? singleDepartment.kind.id : null,
					userResponsibility: singleDepartment.userResponsibility?.id
						? singleDepartment.userResponsibility.id
						: null,
					departmentContacts: singleDepartment.departmentContacts ? singleDepartment.departmentContacts : [],
				};
			}),
			travelOrganizationGovernmentApproval: this.organizationService.travelOrganizationGovernmentApproval(),
		};
	}

	private checkIfHqExist(dataSend): boolean {
		if (dataSend.address.some((obj) => obj.kind === AddressKind.HEADQUATERS)) {
			return true;
		}
		this.infobox.error(
			'Siedziba organizacji musi być uzupełniona. Upewnij się, że adres firmowy został wprowadzony poprawnie',
		);

		return false;
	}

	private doAdd(dataSend): void {
		if (!this.checkIfHqExist(dataSend)) return this.loader.hide();
		this.bag.post(this.apiUrlAdd, dataSend, (result, error) => {
			if (error) {
				return;
			}
			this.organizationService.organizationData.id = result.id;
			this.afterSend(result, error);
		});
	}

	private doEdit(dataSend): void {
		if (!this.checkIfHqExist(dataSend)) return this.loader.hide();
		this.bag.put(this.apiUrl, dataSend, (result, error) => {
			this.afterSend(result, error);
		});
	}

	private afterSend(result, error): void {
		this.loader.hide();
		if (error) {
			return;
		}
		this.organizationService.pageTitle = this.organizationService.pageTitleGenerate();
		this.router.navigate([this.parentComponent, this.organizationService.organizationData.id]);
		this.pageFunction = PageFunction.EDIT;
		this.editable = false;
		this.infobox.success('Zapisano');
	}
}
